import { ImpersonationAlert } from "@/components";
import { routes } from "@/helpers";
import { useAllRights } from "@/hooks";
import { applicationErrorActions } from "@/store";
import { Button, Space, Typography } from "antd";
import { any, exact, func, number, oneOfType, string } from "prop-types";
import { useIntl } from "react-intl";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { StringParam, useQueryParam } from "use-query-params";
import { StyledResult } from "./styled";

const DEFAULT_STATUS_ICON = 500;
const SUPPORTED_ERROR_CODES = [403, 404, 500];
const HELP_DESK_URL =
  "https://milwaukeetool.service-now.com/sp?id=sc_cat_item&sys_id=a867c6b1dbe2f3402494d211ce9619e0";
const SERVICE_DESK_EMAIL = "mailto:ITServiceDesk@milwaukeetool.com";

const { Text, Link } = Typography;

export const ErrorResult = ({ error, resetErrorBoundary }) => {
  const [referrerUrl] = useQueryParam("referrerUrl", StringParam);
  const intl = useIntl();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const rights = useAllRights();

  const statusCode = error?.response?.status;
  const isSupportedError = SUPPORTED_ERROR_CODES.includes(statusCode);

  const getErrorMessageId = () => {
    const suffix = "errors";
    let id;

    switch (statusCode) {
      case 403:
        id = "unauthorized";
        break;
      case 404:
        id = "pageNotFound";
        break;
      case 500:
      default:
        id = "serverError";
        break;
    }

    return {
      id: `${suffix}.${id}`,
    };
  };

  const goToDashboard = () => {
    resetErrorBoundary && resetErrorBoundary();
    dispatch(applicationErrorActions.resetApplicationError());
    navigate(routes.dashboard);
  };

  const resetImpersonationClicked = () => {
    resetErrorBoundary && resetErrorBoundary();
    dispatch(applicationErrorActions.resetApplicationError());
    navigate(referrerUrl || routes.dashboard);
  };

  return (
    <StyledResult
      status={isSupportedError ? statusCode : DEFAULT_STATUS_ICON}
      title={statusCode || intl.formatMessage({ id: "general.error" })}
      subTitle={intl.formatMessage(getErrorMessageId())}
      extra={
        <Space direction="vertical" size="large">
          <Text type="secondary">
            {intl.formatMessage({ id: "errorPage.contactText" })}
            <Link data-test-id="error-result-open-ticket" href={HELP_DESK_URL}>
              &nbsp;
              {intl.formatMessage({ id: "errorPage.clickHere" })}
            </Link>
            &nbsp;
            {intl.formatMessage({ id: "errorPage.emailText" })}
            <Link
              data-test-id="error-result-email-IT"
              href={SERVICE_DESK_EMAIL}
            >
              &nbsp;
              {intl.formatMessage({ id: "errorPage.helpDeskEmail" })}
            </Link>
          </Text>
          {rights.canAccessViper && (
            <Button
              type="primary"
              data-test-id="error-result-go-home"
              onClick={goToDashboard}
            >
              {intl.formatMessage({ id: "errorPage.goToDashboard" })}
            </Button>
          )}

          {statusCode === 403 && (
            <ImpersonationAlert onCancelClick={resetImpersonationClicked} />
          )}
        </Space>
      }
    />
  );
};

ErrorResult.propTypes = {
  error: oneOfType([
    any,
    exact({
      status: number.isRequired,
      message: string.isRequired,
    }),
  ]),
  resetErrorBoundary: func,
};
